import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartDataResponse } from '../types';
import { transformToChartData, CHART_CONFIGS, fillMissingDates, convertToPercentage, convertToMinutes } from '../utils';

type OverlayChartProps = {
  chartData: ChartDataResponse;
  selectedMetrics: Set<string>;
};

export const OverlayChart: React.FC<OverlayChartProps> = ({ chartData, selectedMetrics }) => {
  if (selectedMetrics.size === 0) {
    return null;
  }

  return (
    <div className="chart-container mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">重ね合わせグラフ</h3>
      <div style={{ width: '100%', height: 500 }}>
        <ResponsiveContainer>
          <LineChart margin={{ top: 20, right: 100, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="date"
              type="category"
              allowDuplicatedCategory={false}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              tick={{ fill: '#374151', fontSize: 12 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            {Array.from(selectedMetrics).map((dataKey, index) => {
              const config = CHART_CONFIGS.find(c => c.dataKey === dataKey);
              if (!config) return null;
              
              const transformedData = transformToChartData(chartData, config.dataKey as any);
              const filledData = fillMissingDates(transformedData, chartData.start_date, chartData.end_date);
              const processedData = filledData.map(point => ({
                ...point,
                value: point.value !== null && point.value !== undefined
                  ? (config.dataKey.includes('rate') || config.dataKey === 'sleep_efficiency')
                    ? convertToPercentage(point.value)
                    : (config.dataKey.includes('time') && config.unit === '分')
                      ? convertToMinutes(point.value)
                      : point.value
                  : null,
              }));

              return (
                <React.Fragment key={dataKey}>
                  <YAxis 
                    yAxisId={dataKey}
                    orientation={index % 2 === 0 ? 'left' : 'right'}
                    axisLine={{ stroke: config.color }}
                    tickLine={{ stroke: config.color }}
                    tick={{ fill: config.color, fontSize: 10 }}
                    label={{ 
                      value: `${config.title}(${config.unit})`, 
                      angle: index % 2 === 0 ? -90 : 90, 
                      position: index % 2 === 0 ? 'insideLeft' : 'insideRight',
                      style: { fill: config.color, fontSize: 11 }
                    }}
                  />
                  <Line
                    yAxisId={dataKey}
                    data={processedData}
                    type="monotone"
                    dataKey="value"
                    stroke={config.color}
                    strokeWidth={2}
                    dot={{ fill: config.color, strokeWidth: 2, r: 3 }}
                    name={config.title}
                    connectNulls={false}
                  />
                </React.Fragment>
              );
            })}
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const date = payload[0].payload.date;
                  return (
                    <div className="bg-white p-3 border border-gray-300 rounded-md shadow-lg">
                      <p className="font-medium mb-2">{`日付: ${date}`}</p>
                      {payload.map((entry: any, index: number) => {
                        const config = CHART_CONFIGS.find(c => c.dataKey === entry.dataKey || entry.name === c.title);
                        if (!config || entry.value === null) return null;
                        return (
                          <p key={index} style={{ color: entry.stroke }}>
                            {`${entry.name}: ${entry.value}${config.unit}`}
                          </p>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
